/* ============================================================
   Foo Hung Curios — Predictive Search Drawer
   ------------------------------------------------------------
   This file does two things:
   1. <search-drawer> custom element — predictive search XHR
      and result rendering. (Behavior unchanged from prior
      version aside from minor safety tweaks.)
   2. Opener controller — handles open/close on the
      .search_popup_wrapper#search--drawer modal, including:
        • body scroll lock while open
        • Escape closes
        • backdrop click closes
        • autofocus the input on open
        • aria-hidden + inert sync for accessibility
   ============================================================ */

class SearchDrawer extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input[type="text"]');
        this.reset = this.querySelector('.search_drawer_reset');
        this.emptyWrapper = this.querySelector('.empty_search_drawer');
        this.resultWrapper = this.querySelector('.result_search_drawer');
        this.resultInner = this.resultWrapper.querySelector('.result_search_inner_popup');
    }

    connectedCallback() {
        if (this.input) {
            // Initial sizing so the input has a reasonable width
            // before the user types anything.
            this.syncInputSize();

            this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));

            // Live width tracking — fires on every keystroke so the
            // inline "Clear" button moves immediately with the text,
            // not waiting for the debounced search. Pairs with the
            // CSS `width: auto` so the input collapses to fit text.
            this.input.addEventListener('input', () => this.syncInputSize());

            this.input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const q = this.input.value.trim();
                    if (q.length > 0) {
                        window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}&options[prefix]=last`;
                    }
                }
            });
        }
        this.reset?.addEventListener('click', this.handleReset.bind(this));
    }

    /**
     * Sets the HTML `size` attribute on the input to match its
     * current text length (with a sensible minimum). The `size`
     * attribute resizes the input to roughly that many characters
     * wide and is universally supported, unlike CSS `field-sizing`.
     * Combined with `width: auto` in CSS, this lets the inline
     * "Clear" affordance sit immediately after the typed word.
     */
    syncInputSize() {
        if (!this.input) return;
        // Floor at the placeholder length so an empty input still
        // shows the full "Search for an item..." hint without
        // collapsing oddly.
        const text = this.input.value || this.input.placeholder || '';
        const length = Math.max(text.length, 4);
        this.input.setAttribute('size', String(length));
    }

    debounce(fn, delay) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    handleReset(e) {
        e.preventDefault();
        this.input.value = '';
        this.syncInputSize();
        this.showEmpty(true);
        // Return focus to the input so the user can keep typing.
        this.input.focus();
    }

    handleInput() {
        this.input.value = this.input.value.replace(/[^a-zA-Z0-9 _-]/g, '');
        const q = this.input.value.trim();

        if (q.length === 0) {
            this.showEmpty(true);
            return;
        }

        fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page`)
            .then((res) => res.json())
            .then((data) => this.renderResults(data.resources.results))
            .catch((err) => console.error('Error fetching results:', err));
    }

    showEmpty(state) {
        this.emptyWrapper.style.display = state ? 'flex' : 'none';
        this.resultWrapper.style.display = state ? 'none' : 'block';
        // Add a state class on the wrapper so CSS can adapt the layout
        // when results are showing — e.g. hide the Popular Collections
        // side panel and let the results take full width.
        const wrapper = document.getElementById('search--drawer');
        if (wrapper) {
            wrapper.classList.toggle('has_results', !state);
        }
    }

    renderResults(results) {
        const { products = [], collections = [], articles = [], pages = [], queries = [] } = results;

        this.showEmpty(false);
        this.resultInner.innerHTML = '';

        // Build the search-page URL for the bottom CTA. The
        // predictive-search modal funnels users to the full search
        // page for "see all results" rather than offering per-type
        // filtered escapes.
        const q = this.input.value.trim();
        const encodedQ = encodeURIComponent(q);
        const searchUrlAll = `${window.Shopify.routes.root}search?q=${encodedQ}&options[prefix]=last`;

        // Per-section "See all" links removed — the persistent
        // bottom CTA ("See all results for [query] →") handles the
        // escape-to-full-results action across all result types.
        const addGroup = (title, html) => {
            const wrap = document.createElement('div');
            wrap.classList.add('search_group');
            wrap.innerHTML = `<h6>${title}</h6>${html}`;
            this.resultInner.appendChild(wrap);
        };

        if (queries.length) {
            addGroup('Suggestions', queries.map((sq) => `
        <div class="query_card">
          <p><a href="${sq.url}" aria-label="${sq.text}">${sq.styled_text}</a></p>
        </div>
      `).join(''));
        }

        if (products.length) {
            addGroup('Products', products.map((p) => `
        <div class="search_result_product_card">
          <div class="search_result_product_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${p.url}" aria-label="${p.title}"><img src="${p.featured_image?.url}" alt="${p.title}"></a>
          </div>
          <div class="search_result_product_card_info">
            <p><a href="${p.url}" aria-label="${p.title}">${p.title}</a></p>
            <div class="search_result_product_price">
              <span>${Shopify.formatMoney(p.price_min)}</span>
              ${p.compare_at_price_min > p.price_min ? `<s>${Shopify.formatMoney(p.compare_at_price_min)}</s>` : ''}
            </div>
          </div>
        </div>
      `).join(''));
        }

        if (collections.length) {
            addGroup('Collections', collections.map((c) => `
        <div class="search_result_collection_card">
          <p><a href="${c.url}" aria-label="${c.title}">${c.title}</a></p>
        </div>
      `).join(''));
        }

        if (pages.length) {
            addGroup('Pages', pages.map((pg) => `
        <div class="search_result_page_card">
          <p><a href="${pg.url}" aria-label="${pg.title}">${pg.title}</a></p>
        </div>
      `).join(''));
        }

        if (articles.length) {
            addGroup('Articles', articles.map((a) => `
        <div class="search_result_article_card">
          <div class="search_result_article_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${a.url}" aria-label="${a.title}"><img src="${a.image}" alt="${a.title}"></a>
          </div>
          <div class="search_result_article_info">
            <p>${a.title}</p>
            <a href="${a.url}" aria-label="${a.title}">Read More</a>
          </div>
        </div>
      `).join(''));
        }

        const hasAnyResults = queries.length || collections.length || products.length || articles.length || pages.length;

        if (!hasAnyResults) {
            this.resultInner.innerHTML = `<h6 class="empty_results_heading">No results found matching your query.</h6>`;
            return;
        }

        // Bottom overall "See all results for [query] →" — final escape
        // hatch when results exist but the user wants the full search page.
        const seeAllWrap = document.createElement('div');
        seeAllWrap.className = 'search_see_all_results';
        // Escape the query for safe insertion into HTML. We use textContent
        // assignment for the query span specifically to defuse any HTML.
        seeAllWrap.innerHTML = `
            <a href="${searchUrlAll}">
                See all results for <strong></strong>
            </a>
        `;
        seeAllWrap.querySelector('strong').textContent = `"${q}"`;
        this.resultInner.appendChild(seeAllWrap);
    }
}

customElements.define('search-drawer', SearchDrawer);

/* ============================================================
   Opener / closer controller
   ------------------------------------------------------------
   Many themes use a generic [data-drawer="..."] system that
   toggles the `.serach_active` class on the wrapper. We don't
   want to fight that; we layer on top of it. This controller:
     • observes class changes on the wrapper
     • on open → locks body scroll, focuses input, unsets aria-hidden
     • on close → restores scroll, restores focus to the trigger,
                  re-sets aria-hidden
     • adds Escape and backdrop-click as close paths
   ============================================================ */
(function initSearchDrawerOpener() {
    const ACTIVE_CLASS = 'serach_active'; // preserved spelling from existing theme
    const drawer = document.getElementById('search--drawer');
    if (!drawer) return;

    const input = drawer.querySelector('#search_drawer_input');
    const closeBtn = drawer.querySelector('.search_popup_close');
    const innerPanel = drawer.querySelector('.search_popup_inner');

    let lastTrigger = null;
    let scrollY = 0;

    const isOpen = () => drawer.classList.contains(ACTIVE_CLASS);

    const lockBody = () => {
        scrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
    };

    const unlockBody = () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
    };

    const openDrawer = () => {
        if (isOpen()) return;
        lastTrigger = document.activeElement;
        drawer.classList.add(ACTIVE_CLASS);
        drawer.setAttribute('aria-hidden', 'false');
        lockBody();
        // Reset internal scroll so the user always re-opens at the top of
        // the modal, regardless of where they left off in a previous session.
        const innerBlock = drawer.querySelector('.search_popup_inner-block');
        if (innerBlock) innerBlock.scrollTop = 0;
        // Focus the input after the entry transition starts so the keyboard
        // appears smoothly on mobile. The blinking caret is already visible
        // because focus is set before the user perceives the panel as "ready".
        requestAnimationFrame(() => {
            setTimeout(() => input?.focus({ preventScroll: true }), 80);
        });
    };

    const closeDrawer = () => {
        if (!isOpen()) return;
        drawer.classList.remove(ACTIVE_CLASS);
        drawer.setAttribute('aria-hidden', 'true');
        unlockBody();
        // Restore focus to whatever opened the drawer (usually the header search icon).
        if (lastTrigger && typeof lastTrigger.focus === 'function') {
            lastTrigger.focus({ preventScroll: true });
        }
    };

    // --- Wire up triggers --------------------------------------------------
    // The theme uses [data-drawer="search_drawer"] on the header search icon.
    document.querySelectorAll('[data-drawer="search_drawer"]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            openDrawer();
        });
    });

    // Close button inside the panel.
    closeBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
    });

    // Backdrop click — only when the click lands on the wrapper itself,
    // not bubbling up from the panel.
    drawer.addEventListener('click', (e) => {
        if (e.target === drawer) closeDrawer();
    });

    // Escape key — global, only acts when the drawer is open.
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) {
            e.preventDefault();
            closeDrawer();
        }
    });

    // Initial aria state for screen readers.
    drawer.setAttribute('aria-hidden', isOpen() ? 'false' : 'true');

    // If something *else* in the theme toggles the active class (e.g. a
    // generic drawer manager), keep aria-hidden + body lock in sync.
    const obs = new MutationObserver(() => {
        const open = isOpen();
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (open && document.body.style.position !== 'fixed') {
            lockBody();
        } else if (!open && document.body.style.position === 'fixed') {
            unlockBody();
        }
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
})();